import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from '@jetbrains/ring-ui/components/select/select';
import InlineLoader from '@jetbrains/ring-ui/components/loader-inline/loader-inline';
import classNames from 'classnames';
import List from '@jetbrains/ring-ui/components/list/list';
import { debounce } from 'lodash';
import { useAllSourcePluginsSearch } from '@modules/plugins/use-all-source-plugins-search';
import { Required } from '@common/form/important-asterisk';
import { assignTestId } from '@common/infra/tests/assign-test-id';
import { PluginSelectorAnchor } from './plugin-selector-anchor/plugin-selector-anchor';
import { PluginSelectorItem } from './plugin-selector-item/plugin-selector-item';
import styles from './plugin-selector.css';
export function PluginSelector(props) {
    var _a, _b;
    const { pluginData, onPluginDataChange, isRestrictedMode, fetchEnabled, className } = props;
    const [inputFilterValue, setInputFilterValue] = useState((_b = (_a = pluginData === null || pluginData === void 0 ? void 0 : pluginData.pluginDefinition.name) !== null && _a !== void 0 ? _a : pluginData === null || pluginData === void 0 ? void 0 : pluginData.pluginDefinition.id) !== null && _b !== void 0 ? _b : '');
    const [pluginsFilter, setPluginsFilter] = useState(inputFilterValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceFilterValue = useCallback(debounce((filterValue) => setPluginsFilter(filterValue), 300), []);
    useEffect(() => {
        debounceFilterValue(inputFilterValue);
    }, [inputFilterValue, debounceFilterValue]);
    const { plugins, isFetching, isLoading } = useAllSourcePluginsSearch(pluginsFilter, isRestrictedMode, fetchEnabled);
    const selectItems = useMemo(() => {
        const itemsFromPlugins = [];
        const exactMatchedPlugins = [];
        plugins.forEach(plugin => {
            const pluginSelectItem = pluginModelToSelectItem(plugin.key, plugin.label, convertType(plugin.source));
            if (plugin.label.toLowerCase() === inputFilterValue.trim().toLowerCase() ||
                plugin.key.toLowerCase() === inputFilterValue.trim().toLowerCase()) {
                exactMatchedPlugins.push(pluginSelectItem);
            }
            else {
                itemsFromPlugins.push(pluginSelectItem);
            }
        });
        if (exactMatchedPlugins.length) {
            itemsFromPlugins.unshift(...exactMatchedPlugins);
        }
        else if (inputFilterValue.trim().length && !isRestrictedMode) {
            itemsFromPlugins.push(...createCustomPluginItems(inputFilterValue));
        }
        return itemsFromPlugins;
    }, [plugins, inputFilterValue, isRestrictedMode]);
    const handleSelect = useCallback((item) => {
        var _a, _b;
        if (item) {
            setInputFilterValue((_b = (_a = item.pluginData.pluginDefinition.name) !== null && _a !== void 0 ? _a : item.pluginData.pluginDefinition.id) !== null && _b !== void 0 ? _b : '');
            onPluginDataChange(item.pluginData);
        }
    }, [onPluginDataChange]);
    const selectedItem = useMemo(() => {
        if (!pluginData) {
            return undefined;
        }
        return {
            key: pluginData.pluginDefinition.id,
            label: pluginData.pluginDefinition.name,
            pluginData
        };
    }, [pluginData]);
    const [selectRef, setSelectRef] = useState(null);
    const renderAnchor = useCallback((anchorProps) => (_jsx(PluginSelectorAnchor, { ...anchorProps, value: inputFilterValue, selectRef: selectRef, onValueChange: setInputFilterValue, className: styles.select, pluginData: pluginData })), [inputFilterValue, selectRef, setInputFilterValue, pluginData]);
    return (_jsxs("div", { className: classNames(styles.wrapper, className), ...assignTestId('plugin-selector'), children: [_jsx("p", { className: styles.label, children: Required('Plugin name or ID') }), isLoading && (_jsx("div", { className: styles.loaderWrapper, children: _jsx(InlineLoader, { className: styles.loader }) })), !isLoading && (_jsx(Select, { ref: setSelectRef, selected: selectedItem, onChange: handleSelect, data: selectItems, loading: isFetching, type: Select.Type.CUSTOM, maxHeight: 500, customAnchor: renderAnchor }))] }));
}
function createCustomPluginItems(pluginId) {
    return [
        pluginModelToSelectItem(pluginId, undefined, 'bundled'),
        pluginModelToSelectItem(pluginId, undefined, 'marketplace'),
        pluginModelToSelectItem(pluginId, undefined, 'internal-repo')
    ];
}
function pluginModelToSelectItem(pluginId, pluginName, type) {
    const pluginData = { pluginDefinition: { id: pluginId, name: pluginName }, type };
    return {
        rgItemType: List.ListProps.Type.CUSTOM,
        key: pluginId,
        label: pluginName,
        template: _jsx(PluginSelectorItem, { pluginData: pluginData }),
        pluginData,
        ...assignTestId('plugin-selector-item', type)
    };
}
function convertType(pluginSource) {
    switch (pluginSource) {
        case 'BUNDLED':
            return 'bundled';
        case 'MARKETPLACE':
            return 'marketplace';
        case 'TBE':
            return 'internal-repo';
        default:
            return assert_exhaustive(pluginSource);
    }
}
